import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Rocket, Volume2, VolumeX, User, BarChart3 } from 'lucide-react';
import { useGameStore } from '../store/gameStore';
import StarfieldBackground from '../components/effects/StarfieldBackground';
import Button from '../components/common/Button';

export default function StartScreen() {
  const navigate = useNavigate();
  const { currentProfile, soundEnabled, toggleSound } = useGameStore();

  const handleLaunch = () => {
    if (currentProfile) {
      navigate('/levels');
    } else {
      navigate('/profiles');
    }
  };

  return (
    <div className="relative min-h-screen flex flex-col items-center justify-center p-4">
      <StarfieldBackground />
      
      <motion.div
        initial={{ scale: 0, rotate: -180 }}
        animate={{ scale: 1, rotate: 0 }}
        transition={{ type: "spring", duration: 1 }}
        className="relative z-10 mb-8"
      >
        <div className="relative">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
            className="absolute inset-0 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 blur-xl opacity-50"
          />
          <Rocket className="w-32 h-32 text-white relative z-10" />
        </div>
      </motion.div>

      <motion.h1
        initial={{ y: -50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.3 }}
        className="text-6xl md:text-8xl font-bold mb-4 text-center relative z-10"
      >
        <span className="bg-gradient-to-r from-purple-400 via-pink-400 to-purple-400 bg-clip-text text-transparent">
          Space Math Quest
        </span>
      </motion.h1>

      <motion.p
        initial={{ y: 50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.5 }}
        className="text-xl text-gray-300 mb-8 text-center relative z-10"
      >
        Master multiplication across the galaxy!
      </motion.p>

      {currentProfile && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7 }}
          className="mb-6 text-center relative z-10"
        >
          <p className="text-gray-400 mb-2">Playing as</p>
          <p className="text-2xl font-semibold text-white">{currentProfile.name}</p>
        </motion.div>
      )}

      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 0.9, type: "spring" }}
        className="relative z-10"
      >
        <Button
          size="lg"
          gradient
          onClick={handleLaunch}
          className="text-xl px-12 py-6"
        >
          🚀 Launch Adventure
        </Button>
      </motion.div>

      <motion.div
        initial={{ y: 50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 1.1 }}
        className="mt-12 flex justify-center gap-4 z-10"
      >
        <Button
          variant="secondary"
          size="md"
          onClick={toggleSound}
        >
          {soundEnabled ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
        </Button>
        
        <Button
          variant="secondary"
          size="md"
          onClick={() => navigate('/profiles')}
        >
          <User className="w-5 h-5" />
        </Button>
        
        {currentProfile && (
          <Button
            variant="secondary"
            size="md"
            onClick={() => navigate('/statistics')}
          >
            <BarChart3 className="w-5 h-5" />
          </Button>
        )}
      </motion.div>

      <motion.footer
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.3 }}
        className="fixed bottom-2 text-center text-sm text-gray-500 z-10"
      >
        Created with ❤️ by{' '}
        <a 
          href="https://huseyindemirtas.com/" 
          target="_blank" 
          rel="noopener noreferrer"
          className="text-purple-400 hover:text-purple-300 transition-colors"
        >
          Hüseyin Demirtaş
        </a>
        {' '}in İstanbul
      </motion.footer>
    </div>
  );
}